import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GraduationCap, ChevronDown, ChevronUp, Target } from "lucide-react";

const pct = (c, a) => (a > 0 ? Math.round((c / a) * 100) : 0);
const scoreColor = (p) => (p >= 80 ? "#4caf50" : p >= 50 ? "#d4a017" : "#c0392b");
const shortDate = (s) => (s && s.length >= 10 ? `${parseInt(s.slice(8, 10), 10)}/${parseInt(s.slice(5, 7), 10)}` : s || "");

function ChildStats({ data }) {
  const [open, setOpen] = useState(false);
  const total = pct(data.total_correct, data.total_answered);
  const latest = data.history[0];
  return (
    <li className="card-soft overflow-hidden" data-testid={`quiz-stats-${data.child.toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-3 flex items-center gap-3 hover:bg-[color:var(--bg-elevated)] transition"
        data-testid={`quiz-stats-toggle-${data.child.toLowerCase()}`}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm text-white"
          style={{ background: scoreColor(total) }}
        >
          {total}%
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{data.child}</p>
          <p className="text-xs text-[color:var(--text-tertiary)] mt-0.5 truncate">
            {data.rounds} förhör · {data.total_correct}/{data.total_answered} rätt totalt
            {latest && <> · senast {shortDate(latest.date)}</>}
          </p>
        </div>
        {open
          ? <ChevronUp size={16} className="text-[color:var(--text-tertiary)]" />
          : <ChevronDown size={16} className="text-[color:var(--text-tertiary)]" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {data.tricky_words.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5 flex items-center gap-1">
                <Target size={11} /> Ord som fastnat
              </p>
              <div className="flex flex-wrap gap-1.5">
                {data.tricky_words.map((w) => (
                  <span
                    key={w.word}
                    title={w.facit ? `Facit: ${w.facit}` : undefined}
                    className="text-xs px-2 py-1 rounded-full bg-[color:var(--bg-elevated)]"
                    data-testid={`tricky-word-${data.child.toLowerCase()}`}
                  >
                    {w.word}
                    {w.facit && <span className="text-[color:var(--text-tertiary)]"> — {w.facit}</span>}
                    {w.count > 1 && (
                      <span className="ml-1 font-semibold" style={{ color: "#c0392b" }}>×{w.count}</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">
              Senaste förhören
            </p>
            <ul className="space-y-1">
              {data.history.map((h, i) => {
                const p = pct(h.correct, h.answered);
                return (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <span className="tabular-nums text-[color:var(--text-tertiary)] w-9 flex-shrink-0">{shortDate(h.date)}</span>
                    <span className="flex-1 min-w-0 truncate">
                      {h.summary}
                      {h.quiz_type === "begrepp" && (
                        <span className="ml-1 text-[9px] px-1 py-0.5 rounded uppercase tracking-wider bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]">begrepp</span>
                      )}
                    </span>
                    <span className="tabular-nums font-semibold flex-shrink-0" style={{ color: scoreColor(p) }}>
                      {h.correct}/{h.answered}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}

export function QuizStatsCard() {
  const [children, setChildren] = useState([]);

  useEffect(() => {
    api.get("/quiz-stats")
      .then(({ data }) => setChildren(data.children || []))
      .catch(() => {});
  }, []);

  if (children.length === 0) return null;

  return (
    <section data-testid="quiz-stats-card" className="px-4 mb-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 flex items-center gap-1.5">
        <GraduationCap size={14} />
        Förhörsstatistik
      </h2>
      <ul className="space-y-2">
        {children.map((c) => (
          <ChildStats key={c.child} data={c} />
        ))}
      </ul>
    </section>
  );
}
